"use client";
import { JSX } from "react";
import {exo2Bold, exo2SemiBold, ralewayBold} from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import Button from "@@/components/ui/Button";
import Image, {StaticImageData} from "next/image";
import LogoStrokeBlue from "@@/public/img/shared/logo_stroke_blue.svg";
import Logo from "@@/public/img/logo_mediawind.svg";
import Link from "next/link";

interface Props {
    title: string;
    titleish1?: boolean;
    subtitle?: string;
    text: string;
    cta?: string;
    ctaStyle?: {
        bgColor: string;
        textColor: string;
        className: string;
    }
    ctaUrl?: string;
    sectionId?: string;
    className?: string;
    img?: StaticImageData;
    img4k?: StaticImageData;
    imgClassName?: string;
    imgAlt?: string;
    withLogo?: boolean;
    withLogoFilled?: boolean;
    sectionClassName?: string;
    link?: {
        url: string;
        text: string;
    }
}

function formatText(text: string) {
    if (!text) return '';

    let formatted = text.replace(/\n/g, '<br>');

    if (formatted.trim().startsWith('<')) {
        return formatted;
    }

    const nextTagIndex = formatted.indexOf('<', 1);

    if (nextTagIndex === -1) {
        return `<p>${formatted}</p>`;
    }

    const before = formatted.slice(0, nextTagIndex);
    const after = formatted.slice(nextTagIndex);

    return `<p>${before}</p>${after}`;
}

export default function AnimatedCard({ title, subtitle, text, cta, sectionId, img, img4k, className, imgAlt, ctaUrl, ctaStyle, withLogo, withLogoFilled, imgClassName, sectionClassName, titleish1, link }: Props): JSX.Element {
    const imageAnim = useAppearOnScroll<HTMLDivElement>({
        from: "fromBottom",
        duration: 1000,
        delay: 0,
        offset: 0
    });

    const contentAnim = useAppearOnScroll<HTMLDivElement>({
        from: "fromRight",
        duration: 1000,
        delay: 300,
        offset: 0
    });
    const btnAnim = useAppearOnScroll<HTMLDivElement>({
        from: "fromBottom",
        duration: 1000,
        delay: 0,
        offset: 50
    })

    return (
        <section className={`px-11 w-100 overflow-hidden-mobile`} id={sectionId}>
            <div className={`blue-card w-100 position-relative ${className}`}>
                {withLogo && (
                    <Image src={LogoStrokeBlue} alt={`logo`} className={`position-absolute right--4 top-center z-2 mx-w-25-mobile top-5-mobile`} />
                )}
                {withLogoFilled && (
                    <Image src={Logo} alt={`logo`} width={400} className={`position-absolute right--4 top-5 z-2 mx-w-25-mobile`} />
                )}
                <div
                    ref={imageAnim.ref}
                    className={`blue-card-image-container flex-03 position-relative ${sectionClassName} ${imageAnim.className} ${(withLogo || withLogoFilled) ? 'animatedCardImgWithLogo' : ''}`}
                    style={imageAnim.style}
                >
                    {img && img4k && (
                        <ProgressiveImage
                            lowSrc={img}
                            highSrc={img4k}
                            alt={imgAlt || ''}
                            width={img4k.width ?? 1920}
                            height={img4k.height ?? 1080}
                            quality={100}
                            className={`blue-card-image ${imgClassName}`}
                            classLowRes="lowRes"
                        />
                    )}
                </div>
                <div
                    ref={contentAnim.ref}
                    className={`blue-card-content flex-07 ${contentAnim.className}`}
                    style={contentAnim.style}
                >
                    {titleish1 ? (
                        <>
                            <h2 className={`${exo2Bold.className} fs-4`} dangerouslySetInnerHTML={{ __html: title }} />
                            {subtitle && (
                                <h3 className={`${exo2Bold.className} fs-2 color-blue`} dangerouslySetInnerHTML={{ __html: subtitle }} />
                            )}
                        </>
                    ) : (
                        <>
                            <h2 className={exo2Bold.className} dangerouslySetInnerHTML={{ __html: title }} />
                            {subtitle && (
                                <h3 className={exo2SemiBold.className} dangerouslySetInnerHTML={{ __html: subtitle }} />
                            )}
                        </>
                    )}
                    <div dangerouslySetInnerHTML={{__html: formatText(text) }} className={`d-flex flex-column gap-1`}></div>
                    {link && (
                        <Link href={link.url} target={`_blank`}>{link.text}</Link>
                    )}
                    {cta && (
                        <div ref={btnAnim.ref} className={btnAnim.className} style={btnAnim.style}>
                            <Button
                                href={ctaUrl || '/'}
                                title={cta}
                                bgColor={ctaStyle?.bgColor || 'none'}
                                className={ctaStyle?.className || 'secondaryBtn white'}
                                textColor={ctaStyle?.textColor || 'white'}
                            /></div>
                    )}
                </div>
            </div>
        </section>
    );
}